import {useMemo} from 'react';

import {useUzisEndpointQuery, useUzisReportParams} from './uzisReportApi';
import {UzisReportData, UzisReportFilter} from './uzisreporttypes';

type UseUzisReportDataProps = {
  reportUrl: string;
  filters: UzisReportFilter;
};

type UseUzisReportDataReturn = {
  reportData: UzisReportData | undefined;
  isLoading: boolean;
};

type UzisReportVResultDto = {
  proc51: number;
  proc53: number;
  proc55: number;
  proc55other: number;
  proc52: number;
  iopdc: number;
};

type UzisReportVIResultDto = {
  drugStarted: number;
  drugStopped: number;
  drugChanged: number;
};

type UzisReportVIIResultDto = {
  low: number;
  medium: number;
  high: number;
};

type UzisReportVIIIResultDto = {
  polypharmacy: number;
  nti: number;
  hip: number;
  renalInsufficiency: number;
  hepaticInsufficiency: number;
  significantChanges: number;
  intensiveCare: number;
  diabetesMellitus: number;
  epilepsy: number;
  fibrillation: number;
  tumor: number;
  corticosteroids: number;
  parkinson: number;
};

const getMockUzisReportData: () => UzisReportData = () => ({
  tableV: {proc51: 51, proc53: 53, proc55: 55, proc55other: 0, proc52: 52, iopdc: 10},
  tableVLoading: false,
  tableVI: {drugStarted: 5, drugStopped: 3, drugChanged: 7},
  tableVILoading: false,
  tableVII: {low: 10, medium: 20, high: 41},
  tableVIILoading: false,
  tableVIII: {
    polypharmacy: 10,
    nti: 301,
    hip: 5,
    renalInsufficiency: 22,
    hepaticInsufficiency: 95,
    significantChanges: 198,
    intensiveCare: 3000,
    diabetesMellitus: 43,
    epilepsy: 3,
    fibrillation: 84,
    tumor: 33,
    corticosteroids: 32,
    parkinson: 165,
  },
  tableVIIILoading: false,
});

export const useUzisReportData = ({reportUrl, filters}: UseUzisReportDataProps): UseUzisReportDataReturn => {
  const areAllFiltersSet = useMemo(() => filters.dateFrom != null && filters.dateTo != null, [filters.dateFrom, filters.dateTo]);
  const reportParams = useUzisReportParams(filters);

  const v = useUzisEndpointQuery<UzisReportVResultDto>({
    reportUrl,
    path: '/v',
    params: reportParams,
    enabled: areAllFiltersSet,
    warnOnError: 'Backend API for table V not available',
  });

  const vi = useUzisEndpointQuery<UzisReportVIResultDto>({
    reportUrl,
    path: '/vi',
    params: reportParams,
    enabled: areAllFiltersSet,
    warnOnError: 'Backend API for table VI not available',
  });

  const vii = useUzisEndpointQuery<UzisReportVIIResultDto>({
    reportUrl,
    path: '/vii',
    params: reportParams,
    enabled: areAllFiltersSet,
    warnOnError: 'Backend API for table VII not available',
  });

  const viii = useUzisEndpointQuery<UzisReportVIIIResultDto>({
    reportUrl,
    path: '/viii',
    params: reportParams,
    enabled: areAllFiltersSet,
    warnOnError: 'Backend API for table VIII not available',
  });

  const isAnyLoading = v.isLoading || vi.isLoading || vii.isLoading || viii.isLoading;

  const reportData: UzisReportData | undefined = useMemo(() => {
    if (!areAllFiltersSet) return undefined;

    const dataV = v.data;
    const dataVI = vi.data;
    const dataVII = vii.data;
    const dataVIII = viii.data;

    if (!dataV || !dataVI || !dataVII || !dataVIII) return undefined;

    return {
      tableV: {...dataV},
      tableVLoading: v.isLoading,
      tableVI: {...dataVI},
      tableVILoading: vi.isLoading,
      tableVII: {...dataVII},
      tableVIILoading: vii.isLoading,
      tableVIII: {...dataVIII},
      tableVIIILoading: viii.isLoading,
    };
  }, [areAllFiltersSet, v.data, vi.data, vii.data, viii.data, v.isLoading, vi.isLoading, vii.isLoading, viii.isLoading]);

  return {reportData, isLoading: isAnyLoading};
};
